import { IconArrowUp } from "@/assets/icons/geist/IconArrowUp";
import { IconEye } from "@/assets/icons/geist/IconEye";
import { IconEyeOff } from "@/assets/icons/geist/IconEyeOff";
import ChatInputWithPrompts from "@/components/ai/chat/ChatInputWithPrompts";
import { agentModeAtomFamily } from "@/components/ai/chat/state";
import { DeleteAgentExecutionModal } from "@/components/ai/DeleteAgentExecutionModal";
import { DevRunnerSelector } from "@/components/ai/DevRunnerSelector";
import { AgentModeDropdown } from "@/components/ai/prompt-input/AgentModeDropdown";
import { AgentMode, applyAgentModePrefix } from "@/components/ai/prompt-input/agent-mode";
import { AddImageButton, ImageChip, type ImageAttachment } from "@/components/ai/prompt-input/ImageUpload";
import { useImageDrop, useImagePaste } from "@/components/ai/prompt-input/use-image-drop";
import { SupportBundleUnavailableModal } from "@/components/ai/SupportBundleUnavailableModal";
import { Button } from "@/components/flexkit/Button";
import { KeyboardKey } from "@/components/keyboard/KeyboardKey";
import type { CommandSuggestion } from "@/components/podkit/forms/TextareaWithSuggestions";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { Flags } from "@/feature-flags";
import { useAgentExecutionActions } from "@/hooks/use-agent-execution-actions";
import {
    useFlag,
    useFlagAgentImageAttachmentsEnabled,
    useFlagOnaAIEnabled,
    useFlagCommandsAreSkills,
} from "@/hooks/use-flag";
import { type FollowModeState } from "@/hooks/use-follow-mode";
import { useMembership } from "@/hooks/use-membership";
import { usePersistedInput } from "@/hooks/use-persisted-input";
import { useSendToAgentExecution, type PlainAgentExecution } from "@/queries/agent-queries";
import { formatError } from "@/utils/errors";
import { isTouchDevice } from "@/utils/is-touch-device";
import {
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_ImageInput,
    UserInputBlock_Input,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useAtom } from "jotai";
import type React from "react";
import { useCallback, useMemo, useRef, useState, type FC } from "react";
import { v4 as uuidv4 } from "uuid";
import { ChatInputPopover } from "@/components/ai/chat/ChatInputPopover";
import { MCPIntegrationsList } from "@/components/ai/chat/MCPIntegrationsList";
import { MCPIntegrationsButton } from "@/components/ai/chat/MCPIntegrationsButton";

export type NewPendingMessage = {
    id: string;
    // If content is undefined, it means the message with `id` failed to be sent
    // and should be retracted again.
    content?: string;
};

export type ChatInputProps = {
    agentExecution: PlainAgentExecution;
    environmentId?: string;
    placeholder?: string;
    onNewMessage: (message: NewPendingMessage) => void;
    hasMessages: boolean;
    followMode?: FollowModeState;
    autoFocus?: React.TextareaHTMLAttributes<HTMLTextAreaElement>["autoFocus"];
    disableModeSwitching?: boolean;
} & PropsWithClassName;

export const specModeBorderClass =
    "border border-dashed border-content-brand-accent-01/50 transition-colors focus-within:border-content-brand-accent-01";

export const ChatInput: FC<ChatInputProps> = ({
    agentExecution,
    environmentId,
    onNewMessage,
    className,
    placeholder,
    followMode,
    autoFocus,
    disableModeSwitching,
}) => {
    const { value: isOnaAgentCommandsEnabled } = useFlagOnaAIEnabled();
    const { value: isRalphModeEnabled } = useFlag(Flags.RalphModeEnabled, false);
    const { value: isSpecModeEnabled } = useFlag(Flags.SpecModeEnabled, false);
    const { value: commandsAreSkills } = useFlagCommandsAreSkills();
    const { value: mcpIntegrationsUIEnabled } = useFlag(Flags.MCPIntegrationsUIEnabled, false);
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    const sendToAgentExecution = useSendToAgentExecution();
    const { handleStopAgentExecution, downloadSupportBundle, isSupportBundleAvailable } =
        useAgentExecutionActions(agentExecution);
    const { toast } = useToast();

    const storageKey = `agent-execution-input-${agentExecution.id}`;
    const [inputValue, setInputValue] = usePersistedInput(storageKey, "");
    const [showDeleteAgentExecutionModal, setShowDeleteAgentExecutionModal] = useState(false);
    const [showSupportBundleUnavailableModal, setShowSupportBundleUnavailableModal] = useState(false);
    const [agentMode, setAgentMode] = useAtom(agentModeAtomFamily(environmentId ?? ""));
    const [selectedImages, setSelectedImages] = useState<ImageAttachment[]>([]);
    const { value: imageAttachmentsEnabled } = useFlagAgentImageAttachmentsEnabled();

    const organizationId = membership?.organizationId;
    const chatInputRef = useRef<HTMLDivElement>(null);

    // Check if there are any MCP integration statuses
    const hasMCPIntegrations =
        agentExecution.status?.mcpIntegrationStatuses && agentExecution.status.mcpIntegrationStatuses.length > 0;

    const handleImagesSelected = useCallback((images: ImageAttachment[]) => {
        setSelectedImages((prev) => [...prev, ...images]);
    }, []);

    const handleRemoveImage = useCallback((id: string) => {
        setSelectedImages((prev) => {
            const image = prev.find((img) => img.id === id);
            if (image) {
                URL.revokeObjectURL(image.preview);
            }
            return prev.filter((img) => img.id !== id);
        });
    }, []);

    const {
        isDragging,
        isProcessing: isDropProcessing,
        dragHandlers,
    } = useImageDrop(handleImagesSelected, !imageAttachmentsEnabled);
    const { handlePaste, isProcessing: isPasteProcessing } = useImagePaste(
        handleImagesSelected,
        !imageAttachmentsEnabled,
    );
    const isImageProcessing = isDropProcessing || isPasteProcessing;

    // Build available agent modes based on feature flags
    const availableAgentModes = useMemo(() => {
        const modes: AgentMode[] = [AgentMode.Agent];
        if (isSpecModeEnabled) {
            modes.push(AgentMode.Spec);
        }
        if (isRalphModeEnabled) {
            modes.push(AgentMode.Ralph);
        }
        return modes;
    }, [isSpecModeEnabled, isRalphModeEnabled]);

    const showAgentModeDropdown = availableAgentModes.length > 1;

    const onSubmit = useCallback(
        async (text: string, messageId: string, images?: ImageAttachment[]) => {
            if (!agentExecution.id) {
                return;
            }

            // Build inputs array with text and images
            const inputs: UserInputBlock_Input[] = [
                new UserInputBlock_Input({
                    input: {
                        case: "text",
                        value: new UserInputBlock_TextInput({
                            content: text,
                        }),
                    },
                }),
            ];

            // Add images to inputs array
            for (const image of images ?? []) {
                inputs.push(
                    new UserInputBlock_Input({
                        input: {
                            case: "image",
                            value: new UserInputBlock_ImageInput({
                                data: image.data,
                                mimeType: image.mimeType,
                            }),
                        },
                    }),
                );
            }

            // Send single message with all inputs
            await sendToAgentExecution.mutateAsync(
                new SendToAgentExecutionRequest({
                    agentExecutionId: agentExecution.id,
                    userInput: new UserInputBlock({
                        id: messageId,
                        inputs,
                    }),
                }),
            );
        },
        [agentExecution.id, sendToAgentExecution],
    );

    const handleSendMessage = useCallback(
        async (value: string) => {
            const trimmedValue = value.trim();
            if (!trimmedValue) {
                return;
            }

            // Handle built-in commands (only if feature flag is enabled)
            if (isOnaAgentCommandsEnabled) {
                if (trimmedValue === "/clear") {
                    setInputValue("");
                    setShowDeleteAgentExecutionModal(true);
                    return;
                }

                // Handle /skills or /commands based on feature flag
                const manageCommand = commandsAreSkills ? "/skills" : "/commands";
                if (trimmedValue === manageCommand && isOrgAdmin) {
                    setInputValue("");
                    window.open("/settings/agents", "_blank");
                    return;
                }
                if (trimmedValue === "/support-bundle") {
                    setInputValue("");
                    if (isSupportBundleAvailable) {
                        void downloadSupportBundle();
                    } else {
                        setShowSupportBundleUnavailableModal(true);
                    }
                    return;
                }
            }

            if (sendToAgentExecution.isPending) {
                return;
            }

            if (isImageProcessing) {
                toast({
                    title: "Please wait",
                    description: "Image is still being processed",
                });
                return;
            }

            const messageToSend = applyAgentModePrefix(trimmedValue, agentMode);

            const id = uuidv4();
            const imagesToSend = imageAttachmentsEnabled ? [...selectedImages] : undefined;
            try {
                setInputValue("");
                // Clear images before sending (optimistic)
                if (imagesToSend && imagesToSend.length > 0) {
                    setSelectedImages([]);
                }
                onNewMessage({ id, content: trimmedValue });
                await onSubmit(messageToSend, id, imagesToSend);
                // Revoke blob URLs after successful send
                for (const image of imagesToSend ?? []) {
                    URL.revokeObjectURL(image.preview);
                }
            } catch (error) {
                setInputValue(value);
                // Restore images on failure
                if (imagesToSend && imagesToSend.length > 0) {
                    setSelectedImages(imagesToSend);
                }
                onNewMessage({ id, content: undefined });
                toast({
                    title: "Error sending to conversation",
                    description: formatError(error),
                });
            }
        },
        [
            sendToAgentExecution.isPending,
            setInputValue,
            onNewMessage,
            onSubmit,
            toast,
            isOnaAgentCommandsEnabled,
            isOrgAdmin,
            commandsAreSkills,
            downloadSupportBundle,
            isSupportBundleAvailable,
            setShowSupportBundleUnavailableModal,
            agentMode,
            imageAttachmentsEnabled,
            selectedImages,
            isImageProcessing,
        ],
    );

    const onKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                await handleStopAgentExecution();
                return;
            }
            // On touch devices, let Enter insert newlines (users submit via Send button)
            // On desktop, Enter submits and Shift+Enter inserts newlines
            if (e.key === "Enter" && !e.shiftKey && !isTouchDevice()) {
                const input = e.target as HTMLTextAreaElement;
                e.preventDefault();
                e.stopPropagation();
                await handleSendMessage(input.value);
                return;
            }
        },
        [handleStopAgentExecution, handleSendMessage],
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
    };

    const handleSuggestionSelect = useCallback(
        (suggestion: CommandSuggestion) => {
            // Check if this is a builtin command
            if (suggestion.id.startsWith("builtin-")) {
                // Execute builtin commands directly
                if (suggestion.value === "/clear") {
                    setShowDeleteAgentExecutionModal(true);
                    return true; // Prevent default pasting behavior
                }

                // Handle /skills or /commands based on feature flag
                const manageCommand = commandsAreSkills ? "/skills" : "/commands";
                if (suggestion.value === manageCommand && isOrgAdmin) {
                    window.open("/settings/agents", "_blank");
                    return true; // Prevent default pasting behavior
                }

                if (suggestion.value === "/support-bundle") {
                    if (isSupportBundleAvailable) {
                        void downloadSupportBundle();
                    } else {
                        setShowSupportBundleUnavailableModal(true);
                    }
                    return true; // Prevent default pasting behavior
                }
            }
            // For non-builtin commands, allow the default pasting behavior
            return false;
        },
        [
            isOrgAdmin,
            commandsAreSkills,
            downloadSupportBundle,
            isSupportBundleAvailable,
            setShowSupportBundleUnavailableModal,
        ],
    );

    const isInputEmpty = !inputValue.trim() && selectedImages.length === 0;

    // Plan mode styling: dashed purple border instead of default border
    const isSpecMode = agentMode === AgentMode.Spec;

    return (
        <>
            <div
                ref={chatInputRef}
                className={cn(
                    "relative flex w-full flex-col gap-1 rounded-lg bg-surface-pure pb-2 pl-4 pr-0 pt-4 shadow-sm",
                    isSpecMode ? specModeBorderClass : "border border-border-base",
                    isDragging && "ring-2 ring-content-accent ring-offset-2",
                )}
                {...dragHandlers}
            >
                <ChatInputWithPrompts
                    agentExecution={agentExecution}
                    minHeight={40}
                    maxHeight={300}
                    popoverSide="top"
                    popoverSideOffset={20} // top padding + border + some spacing
                    popoverWidthOffset={16} // account for the total padding (only left padding at the moment)
                    popoverAlignOffset={-16} // account for the left padding
                    placeholder={placeholder}
                    onKeyDown={onKeyDown}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    onSuggestionSelect={handleSuggestionSelect}
                    value={inputValue}
                    className={className}
                    data-testid="agent-execution-chat-input"
                    autoFocus={autoFocus}
                    enterKeyHint="enter"
                    disabledMessage={
                        agentMode === AgentMode.Ralph
                            ? "Commands are not available in Ralph mode. Switch to Agent mode to use commands."
                            : undefined
                    }
                />
                <div className="flex items-center justify-between gap-2 pr-2">
                    {/* Left side: Add image button, image chips, and tools button */}
                    <div className="flex flex-1 flex-wrap items-center gap-1">
                        {imageAttachmentsEnabled && <AddImageButton onImagesSelected={handleImagesSelected} />}
                        {imageAttachmentsEnabled &&
                            selectedImages.map((image) => (
                                <ImageChip key={image.id} image={image} onRemove={handleRemoveImage} />
                            ))}
                        {isImageProcessing && (
                            <span className="text-sm text-content-secondary">Processing image...</span>
                        )}
                        {mcpIntegrationsUIEnabled && hasMCPIntegrations && (
                            <ChatInputPopover
                                trigger={
                                    <MCPIntegrationsButton
                                        agentExecution={agentExecution}
                                        organizationId={organizationId}
                                        data-tracking-id="default-tracking-id"
                                    />
                                }
                                content={
                                    <MCPIntegrationsList
                                        organizationId={organizationId}
                                        agentExecution={agentExecution}
                                    />
                                }
                                className="w-96"
                            />
                        )}
                    </div>
                    {/* Right side: Follow mode, dev runner selector, agent mode dropdown, send button */}
                    <div className="flex items-center gap-2">
                        {followMode && (
                            <Tooltip content={followMode.enabled ? "Stop following" : "Follow"}>
                                <Button
                                    variant="ghost"
                                    className="hidden size-9 p-0 md:flex"
                                    LeadingIcon={followMode.enabled ? IconEyeOff : IconEye}
                                    aria-label={followMode.enabled ? "Stop following" : "Follow"}
                                    onClick={() => followMode.toggle()}
                                    data-tracking-id="toggle-follow-mode"
                                />
                            </Tooltip>
                        )}

                        <DevRunnerSelector environmentId={environmentId} />

                        {showAgentModeDropdown && (
                            <AgentModeDropdown
                                mode={agentMode}
                                onModeChange={setAgentMode}
                                availableModes={availableAgentModes}
                                disabled={disableModeSwitching}
                            />
                        )}

                        {/* Send Button */}
                        <Tooltip
                            content={
                                <div className="flex flex-row items-center gap-2">
                                    Send
                                    <div className="flex items-center gap-1">
                                        <KeyboardKey>Enter</KeyboardKey>
                                    </div>
                                </div>
                            }
                        >
                            <Button
                                id="send-button"
                                className="size-9 p-0"
                                LeadingIcon={IconArrowUp}
                                aria-label="Send message"
                                onClick={() => handleSendMessage(inputValue)}
                                disabled={isInputEmpty || sendToAgentExecution.isPending || isImageProcessing}
                                data-tracking-id="send-to-agent-execution-chat-input"
                            />
                        </Tooltip>
                    </div>
                </div>
            </div>

            <DeleteAgentExecutionModal
                agentExecution={agentExecution}
                open={showDeleteAgentExecutionModal}
                onClose={() => setShowDeleteAgentExecutionModal(false)}
                inputValue={inputValue}
            />

            <SupportBundleUnavailableModal
                open={showSupportBundleUnavailableModal}
                onClose={() => setShowSupportBundleUnavailableModal(false)}
                agentExecution={agentExecution}
            />
        </>
    );
};
